<template>
  <v-row class="import-dropzone">
    <v-col cols="12" md="7">
      <section class="panel upload-panel">
        <h3 class="text-h6 mb-1">{{ title }}</h3>
        <p class="text-body-2 mb-4 panel-muted">{{ subtitle }}</p>
        <v-btn color="primary" class="mb-5" :disabled="loading" @click="$emit('template')">
          <v-icon left>mdi-download</v-icon>
          Descargar plantilla
        </v-btn>

        <div
          class="dropzone"
          :class="{ 'dropzone--over': dragging, 'dropzone--busy': loading }"
          role="button"
          tabindex="0"
          :aria-disabled="loading ? 'true' : 'false'"
          aria-label="Cargar archivo Excel: arrastre el archivo aquí o presione Enter para buscarlo en su equipo"
          @click="pick"
          @keydown.enter.prevent="pick"
          @keydown.space.prevent="pick"
          @dragover.prevent="dragging = !loading"
          @dragleave.prevent="dragging = false"
          @drop.prevent="onDrop"
        >
          <!-- :key reinicia el reproductor al cambiar de animación (nube <-> carga). -->
          <LottiePlayer
            :key="loading ? 'loader' : 'cloud'"
            :animation="loading ? loaderAnimation : cloudAnimation"
            :speed="dragging ? 2.4 : 1"
            class="dropzone__art"
          />
          <div v-if="loading" class="dropzone__title" role="status">{{ busyText }}</div>
          <template v-else>
            <div class="dropzone__title">{{ dragging ? "Suelte el archivo para cargarlo" : "Arrastre su archivo aquí" }}</div>
            <div class="text-body-2 panel-muted">o <span class="dropzone__link">búsquelo en su equipo</span></div>
          </template>
        </div>
      </section>
    </v-col>

    <v-col cols="12" md="5">
      <section class="panel mb-4">
        <h3 class="text-h6 mb-3">Cómo funciona</h3>
        <ol class="steps">
          <li v-for="(step, index) in howItWorks" :key="index">
            <span class="steps__number">{{ index + 1 }}</span>
            <span class="text-body-2">{{ step }}</span>
          </li>
        </ol>
      </section>
      <section class="panel">
        <h3 class="text-h6 mb-3">Tenga en cuenta</h3>
        <ol class="steps">
          <li v-for="(tip, index) in tips" :key="index">
            <span class="steps__number">{{ index + 1 }}</span>
            <span class="text-body-2">{{ tip }}</span>
          </li>
        </ol>
      </section>
    </v-col>
  </v-row>
</template>

<script>
import LottiePlayer from "@/components/LottiePlayer";
import cloudAnimation from "@/assets/lottie/cloud-upload.json";
import loaderAnimation from "@/assets/lottie/loader.json";

// Zona de carga de los importadores V2 (panel "Cargar archivo" + guía). Solo presenta: leer el Excel y las reglas viven en la pantalla.
// Eventos: template (descargar plantilla), pick (abrir el selector de archivos), file (File soltado), invalid (mensaje de por qué no).
export default {
  name: "ImportDropzone",
  components: { LottiePlayer },
  props: {
    title: { type: String, default: "Cargar archivo" },
    subtitle: { type: String, default: "" },
    howItWorks: { type: Array, default: () => [] },
    tips: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    busyText: { type: String, default: "" },
  },
  data() {
    return { dragging: false };
  },
  created() {
    // JSON de Lottie sin reactividad: Vue no necesita observar miles de nodos.
    this.cloudAnimation = cloudAnimation;
    this.loaderAnimation = loaderAnimation;
  },
  methods: {
    pick() {
      if (!this.loading) this.$emit("pick");
    },
    // accept= del input no aplica al soltar: el tipo y la cantidad se validan aquí.
    onDrop(event) {
      this.dragging = false;
      if (this.loading) return;
      const files = event.dataTransfer.files;
      if (files.length !== 1) return this.$emit("invalid", "Suelte un solo archivo Excel.");
      if (!/\.xlsx?$/i.test(files[0].name)) return this.$emit("invalid", "Solo se permiten archivos .xlsx o .xls.");
      return this.$emit("file", files[0]);
    },
  },
};
</script>

<style scoped>
.import-dropzone {
  --imp-accent: #3498db;
  --imp-ink: #1b4f72;
  --imp-tint: #f4f9fd;
  --imp-tint-strong: #e3f0fa;
}

.panel {
  padding: 24px;
  border-radius: 12px;
  background-color: var(--imp-tint);
}

/* La zona de carga ocupa el alto sobrante: alinea el borde inferior con la columna de ayuda. */
.upload-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.upload-panel .v-btn {
  align-self: flex-start;
}

.panel-muted {
  color: rgba(0, 0, 0, 0.6);
}

.dropzone {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 232px;
  padding: 16px;
  border: 2px dashed var(--imp-accent);
  border-radius: 12px;
  background-color: #fff;
  text-align: center;
  cursor: pointer;
  transition: background-color 0.2s ease-out, border-color 0.2s ease-out;
}

/* Solo la zona recibe los eventos de arrastre: sin esto, dragleave parpadea al pasar sobre los hijos. */
.dropzone > * {
  pointer-events: none;
}

.dropzone:hover,
.dropzone--over {
  background-color: var(--imp-tint-strong);
  border-color: var(--imp-ink);
}

.dropzone:focus-visible {
  outline: 3px solid var(--imp-ink);
  outline-offset: 3px;
}

.dropzone--busy {
  cursor: progress;
}

.dropzone__art {
  width: 160px;
  height: 160px;
}

.dropzone__title {
  margin-top: 4px;
  font-size: 1rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
}

.dropzone__link {
  font-weight: 600;
  color: var(--imp-ink);
  text-decoration: underline;
  text-underline-offset: 2px;
}

.steps {
  margin: 0;
  padding: 0;
  list-style: none;
}

.steps li {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.steps li + li {
  margin-top: 14px;
}

.steps__number {
  flex: 0 0 28px;
  height: 28px;
  border-radius: 8px;
  background-color: var(--imp-tint-strong);
  color: var(--imp-ink);
  font-size: 0.875rem;
  font-weight: 700;
  line-height: 28px;
  text-align: center;
}

@media (prefers-reduced-motion: reduce) {
  .dropzone {
    transition: none;
  }
}
</style>
